/**
 * Conversation RPC definitions
 *
 * Authenticated endpoints for conversation operations.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { ResearchConversationPartSchema } from "../admin/conversations.rpc.ts";
import { AuthMiddleware } from "../auth.tags.ts";
import {
  ConversationNotFoundError,
  ConversationOwnershipError,
  DatabaseError,
  NotASceneConversationError,
  SceneEndedError,
  SceneNotEndedError,
  SceneNotFoundError,
  Unauthorized,
  ValidationError,
} from "../errors.ts";

// Re-export ResearchConversationPartSchema for convenience
export { type ResearchConversationPart, ResearchConversationPartSchema } from "../admin/conversations.rpc.ts";

// ============================================================================
// Schemas
// ============================================================================

/**
 * Conversation schema matching database model
 * @since 1.0.0
 */
export const ConversationSchema = Schema.Struct({
  id: Schema.String,
  personId: Schema.NullOr(Schema.String),
  title: Schema.NullOr(Schema.String),
  createdAt: Schema.DateTimeUtc,
  updatedAt: Schema.DateTimeUtc,
});

/**
 * Message schema matching database model
 * @since 1.0.0
 */
export const MessageSchema = Schema.Struct({
  id: Schema.String,
  conversationId: Schema.String,
  role: Schema.Literal("user", "assistant", "tool", "system"),
  content: Schema.Unknown,
  messageIndex: Schema.Number,
  metadata: Schema.NullOr(Schema.Unknown),
  createdAt: Schema.DateTimeUtc,
});

// ============================================================================
// Request/Response Schemas
// ============================================================================

const ConversationListResponse = Schema.Array(ConversationSchema);

const ConversationGetRequest = Schema.Struct({
  conversationId: Schema.String,
});

const ConversationGetResponse = Schema.Struct({
  conversation: ConversationSchema,
  messages: Schema.Array(MessageSchema),
});

const ConversationCreateRequest = Schema.Struct({
  title: Schema.optional(Schema.String),
});

const ConversationUpdateTitleRequest = Schema.Struct({
  conversationId: Schema.String,
  title: Schema.String,
});

const ConversationDeleteRequest = Schema.Struct({
  conversationId: Schema.String,
});

const ConversationDeleteResponse = Schema.Struct({
  success: Schema.Boolean,
});

/**
 * Request schema for streaming a research conversation
 * @since 1.0.0
 */
const ConversationStreamResearchRequest = Schema.Struct({
  conversationId: Schema.String,
  content: Schema.String,
});

// ============================================================================
// Scene Schemas
// ============================================================================

/**
 * The ways a scene can end. Mirrors `EndingKind` in `src/scenes/index.ts`.
 * @since 1.0.0
 */
export const SceneEndingKindSchema = Schema.Literal("earned-open", "warm-closed", "early-ramp");

/**
 * Request schema for starting a scene conversation.
 * @since 1.0.0
 */
export const SceneStartRequest = Schema.Struct({
  sceneKey: Schema.String,
});

/**
 * Response schema for starting a scene conversation. `personaName` is
 * server-authoritative — the client renders it in the chat header with no
 * client-side persona tables.
 * @since 1.0.0
 */
export const SceneStartResponse = Schema.Struct({
  conversation: ConversationSchema,
  sceneCard: Schema.String,
  personaName: Schema.String,
  openingMessage: MessageSchema,
});

/**
 * Request schema for sending a message into a scene conversation.
 * @since 1.0.0
 */
export const SceneSendRequest = Schema.Struct({
  conversationId: Schema.String,
  text: Schema.String.pipe(Schema.maxLength(2000)),
});

/**
 * Stream parts for a scene send. Field names follow the repo's existing part
 * convention (the web guards check `"delta" in part`).
 * @since 1.0.0
 */
export const ScenePartSchema = Schema.Union(
  Schema.Struct({
    type: Schema.Literal("text-delta"),
    delta: Schema.String,
  }),
  Schema.Struct({
    type: Schema.Literal("scene-status"),
    messagesUsed: Schema.Number,
    budget: Schema.Number,
    ended: Schema.Boolean,
    endingKind: Schema.optional(SceneEndingKindSchema),
  }),
  Schema.Struct({
    type: Schema.Literal("stream-done"),
    conversationId: Schema.String,
    messageId: Schema.String,
  }),
);

/**
 * Type for scene stream parts.
 * @since 1.0.0
 */
export type ScenePart = Schema.Schema.Type<typeof ScenePartSchema>;

// ============================================================================
// Reveal Schemas
// ============================================================================

/**
 * One Reveal beat as the mobile app renders it: the guest's actual line this
 * beat anchors on, and Wren's side of that moment in the reveal voice. The
 * markdown on the response is for future share surfaces; the app renders natively
 * from these two fields.
 * @since 1.0.0
 */
export const RevealBeatSchema = Schema.Struct({
  quote: Schema.String,
  narration: Schema.String,
});

/**
 * Type for a single Reveal beat.
 * @since 1.0.0
 */
export type RevealBeat = Schema.Schema.Type<typeof RevealBeatSchema>;

/**
 * Request schema for generating (or re-reading) the Reveal for a conversation.
 * @since 1.0.0
 */
export const ConversationRevealRequest = Schema.Struct({
  conversationId: Schema.String,
});

/**
 * Response schema shared by the freshly-generated and the idempotent stored
 * paths: the structured beats plus the full markdown rendering.
 * @since 1.0.0
 */
export const ConversationRevealResponse = Schema.Struct({
  beats: Schema.Array(RevealBeatSchema),
  markdown: Schema.String,
});

/**
 * Request schema for recording the "felt true" rating on a finished scene. The
 * rating bounds (1–5) are enforced by the payload schema itself.
 * @since 1.0.0
 */
export const ConversationFeltTrueRequest = Schema.Struct({
  conversationId: Schema.String,
  rating: Schema.Int.pipe(Schema.between(1, 5)),
  comment: Schema.optional(Schema.String),
});

/**
 * Response schema for recording the "felt true" rating.
 * @since 1.0.0
 */
export const ConversationFeltTrueResponse = Schema.Struct({
  success: Schema.Boolean,
});

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Conversation RPC group containing conversation endpoints
 *
 * All conversation endpoints require authentication via AuthMiddleware.
 * Handlers verify conversation ownership directly.
 *
 * @since 1.0.0
 */
export class ConversationRpcs extends RpcGroup.make(
  Rpc.make("conversation.list", {
    success: ConversationListResponse,
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("conversation.get", {
    payload: ConversationGetRequest,
    success: ConversationGetResponse,
    error: Schema.Union(
      Unauthorized,
      ConversationNotFoundError,
      ConversationOwnershipError,
      DatabaseError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("conversation.create", {
    payload: ConversationCreateRequest,
    success: ConversationSchema,
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("conversation.updateTitle", {
    payload: ConversationUpdateTitleRequest,
    success: ConversationSchema,
    error: Schema.Union(
      Unauthorized,
      ConversationNotFoundError,
      ConversationOwnershipError,
      DatabaseError,
      ValidationError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("conversation.delete", {
    payload: ConversationDeleteRequest,
    success: ConversationDeleteResponse,
    error: Schema.Union(
      Unauthorized,
      ConversationNotFoundError,
      ConversationOwnershipError,
      DatabaseError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("conversation.streamResearch", {
    payload: ConversationStreamResearchRequest,
    success: ResearchConversationPartSchema,
    error: Schema.Union(
      Unauthorized,
      ConversationNotFoundError,
      ConversationOwnershipError,
      DatabaseError,
    ),
    stream: true,
  }).middleware(AuthMiddleware),
  Rpc.make("conversation.startScene", {
    payload: SceneStartRequest,
    success: SceneStartResponse,
    error: Schema.Union(Unauthorized, SceneNotFoundError, DatabaseError),
  }).middleware(AuthMiddleware),
  Rpc.make("conversation.sendScene", {
    payload: SceneSendRequest,
    success: ScenePartSchema,
    error: Schema.Union(
      Unauthorized,
      ConversationNotFoundError,
      ConversationOwnershipError,
      NotASceneConversationError,
      SceneEndedError,
      DatabaseError,
    ),
    stream: true,
  }).middleware(AuthMiddleware),
  Rpc.make("conversation.reveal", {
    payload: ConversationRevealRequest,
    success: ConversationRevealResponse,
    error: Schema.Union(
      Unauthorized,
      ConversationNotFoundError,
      ConversationOwnershipError,
      SceneNotEndedError,
      DatabaseError,
    ),
  }).middleware(AuthMiddleware),
  Rpc.make("conversation.feltTrue", {
    payload: ConversationFeltTrueRequest,
    success: ConversationFeltTrueResponse,
    error: Schema.Union(
      Unauthorized,
      ConversationNotFoundError,
      ConversationOwnershipError,
      DatabaseError,
    ),
  }).middleware(AuthMiddleware),
) {}
